// Lucky 7 — server clock synchronisation (NTP / Cristian's algorithm).
//
// GOAL: the round countdown must track the SERVER clock exactly, no matter
// what the device's clock says (it may be wrong by minutes, or jump when
// the OS re-syncs). We achieve this by:
//
//   1. Round-tripping GET /api/lucky7/time (a DB-free, lock-gate-free
//      endpoint) and estimating the server time at the request MIDPOINT,
//      assuming symmetric network latency  →  serverTime ≈ tServer + rtt/2.
//      (Cristian's algorithm.)
//
//   2. Anchoring the offset to the browser's MONOTONIC clock,
//      performance.now(), which is immune to wall-clock changes / NTP
//      jumps. So serverNow() = performance.now() + offset, and the
//      countdown can never drift with the device clock.
//
//   3. Taking the BEST of several samples (the one with the lowest RTT has
//      the least network noise, so its midpoint assumption is the most
//      accurate) — the standard NTP "minimum-delay" filter. Re-syncing
//      periodically + whenever the tab regains focus to correct any slow
//      drift between the two monotonic clocks.
//
// HONEST LIMIT: true microsecond sync over HTTP is physically impossible —
// network jitter is milliseconds and browser clocks are clamped to ms /
// sub-ms. In practice this locks the countdown to the server within a few
// milliseconds and keeps it there. The pure math below is unit-tested.

import { getAccessToken } from '@baaligames/shared-ui/lib/api';

// ── Pure, unit-testable math (no I/O) ───────────────────────────────
/** Offset to add to performance.now() to get server epoch ms, from one
 *  sample: server-reported epoch + the two client monotonic timestamps
 *  (ms, performance.now() domain) bracketing the request. */
export function computeOffset(serverEpochMs: number, t0: number, t1: number): number {
  // server clock was read at ~the request midpoint (symmetric-latency
  // assumption); midpoint in the performance.now() domain is (t0+t1)/2.
  return serverEpochMs - (t0 + t1) / 2;
}
/** Current server epoch ms given a monotonic now() reading + offset. */
export function applyOffset(perfNow: number, offset: number): number {
  return perfNow + offset;
}

// ── Live state ──────────────────────────────────────────────────────
let bestOffset = 0;
let bestRtt = Infinity;
let synced = false;

async function oneSample(): Promise<{ offset: number; rtt: number } | null> {
  const tok = getAccessToken();
  const t0 = performance.now();
  try {
    const r = await fetch('/api/lucky7/time', {
      headers: tok ? { authorization: `Bearer ${tok}` } : undefined,
      credentials: 'include',
      cache: 'no-store',
    });
    const t1 = performance.now();
    if (!r.ok) return null;
    const j = await r.json();
    const serverEpochMs: number = j?.data?.now ?? j?.now;
    if (typeof serverEpochMs !== 'number' || !isFinite(serverEpochMs)) return null;
    return { offset: computeOffset(serverEpochMs, t0, t1), rtt: t1 - t0 };
  } catch {
    return null;
  }
}

/** Run a burst of samples; keep the offset from the lowest-RTT sample. */
export async function sync(samples = 6): Promise<boolean> {
  let localOffset = bestOffset;
  let localRtt = synced ? bestRtt : Infinity;
  for (let i = 0; i < samples; i++) {
    const s = await oneSample();
    if (s && s.rtt < localRtt) { localRtt = s.rtt; localOffset = s.offset; }
    await new Promise((res) => setTimeout(res, 50)); // small gap between probes
  }
  if (localRtt < Infinity) {
    bestOffset = localOffset;
    bestRtt = localRtt;
    synced = true;
    return true;
  }
  return false;
}

/** Current server epoch ms (monotonic-anchored). Falls back to Date.now()
 *  only before the first successful sync. */
export function serverNow(): number {
  return synced ? applyOffset(performance.now(), bestOffset) : Date.now();
}
export function isSynced(): boolean { return synced; }
export function lastRtt(): number { return bestRtt; }

/** Start continuous sync: an initial burst, then periodic re-sync + a
 *  re-sync on tab refocus. Returns a stop() cleanup. */
export function startAutoSync(periodMs = 30000): () => void {
  void sync(8); // initial burst (more samples → tighter lock at start)
  const timer = window.setInterval(() => { void sync(4); }, periodMs);
  const onVis = () => { if (document.visibilityState === 'visible') void sync(4); };
  document.addEventListener('visibilitychange', onVis);
  return () => {
    window.clearInterval(timer);
    document.removeEventListener('visibilitychange', onVis);
  };
}
